import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useListCampaignsQuery } from '~/api/endpoints/campaigns'
import { useListTablesQuery } from '~/api/endpoints/prospecting'
import { cx } from '~/lib/cx'

interface Command {
  id: string
  label: string
  group: string
  run: () => void
}

/**
 * ⌘K. The speed story of this console is keyboard, not animation.
 *
 * Deliberately only navigation: a palette that can also activate campaigns is a palette
 * that can start messaging four thousand people because somebody's fuzzy search matched
 * one row further down than they were looking.
 */
export function CommandPalette() {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [cursor, setCursor] = useState(0)
  const { data } = useListCampaignsQuery(undefined, { skip: !open })
  const { data: tables } = useListTablesQuery(undefined, { skip: !open })

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        setOpen((current) => !current)
        setQuery('')
        setCursor(0)
      }
      if (event.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  const commands = useMemo<Command[]>(() => {
    const go = (to: string) => () => {
      navigate(to)
      setOpen(false)
    }
    return [
      { id: 'nav-overview', label: 'Overview', group: 'Go to', run: go('/') },
      { id: 'nav-tables', label: 'Tables', group: 'Go to', run: go('/prospecting') },
      { id: 'nav-recipes', label: 'Recipes', group: 'Go to', run: go('/prospecting/recipes') },
      { id: 'nav-runs', label: 'Runs', group: 'Go to', run: go('/prospecting/runs') },
      { id: 'nav-campaigns', label: 'Campaigns', group: 'Go to', run: go('/campaigns') },
      { id: 'nav-inbox', label: 'Inbox', group: 'Go to', run: go('/inbox') },
      { id: 'nav-accounts', label: 'Accounts', group: 'Go to', run: go('/accounts') },
      ...(data?.items ?? []).map((campaign) => ({
        id: campaign.id,
        label: campaign.name,
        group: 'Campaign',
        run: go(`/campaigns/${campaign.id}`),
      })),
      ...(tables?.items ?? []).map((listing) => ({
        id: listing.table.id,
        label: listing.table.name,
        group: 'Table',
        run: go(`/prospecting/tables/${listing.table.id}`),
      })),
    ]
  }, [data, navigate, tables])

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return commands
    return commands.filter((command) => command.label.toLowerCase().includes(needle))
  }, [commands, query])

  if (!open) return null

  const active = matches[Math.min(cursor, matches.length - 1)]

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 pt-[15vh]"
      onClick={() => setOpen(false)}
    >
      <div
        role="dialog"
        aria-label="Command palette"
        onClick={(event) => event.stopPropagation()}
        className="w-full max-w-md overflow-hidden rounded-md border border-line bg-surface"
        style={{ boxShadow: 'var(--shadow)' }}
      >
        <input
          autoFocus
          value={query}
          placeholder="Go to…"
          aria-label="Search"
          onChange={(event) => {
            setQuery(event.target.value)
            setCursor(0)
          }}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown' || (event.ctrlKey && event.key === 'n')) {
              event.preventDefault()
              setCursor((current) => Math.min(current + 1, matches.length - 1))
            }
            if (event.key === 'ArrowUp' || (event.ctrlKey && event.key === 'p')) {
              event.preventDefault()
              setCursor((current) => Math.max(current - 1, 0))
            }
            if (event.key === 'Enter') {
              event.preventDefault()
              active?.run()
            }
          }}
          className="h-10 w-full border-b border-line bg-transparent px-3 text-sm outline-none placeholder:text-faint"
        />

        <ul className="max-h-72 overflow-y-auto py-1">
          {matches.length === 0 ? (
            <li className="px-3 py-2 text-xs text-dim">Nothing matches “{query}”.</li>
          ) : (
            matches.map((command, index) => (
              <li key={command.id}>
                <button
                  type="button"
                  onMouseEnter={() => setCursor(index)}
                  onClick={command.run}
                  className={cx(
                    'flex w-full items-center justify-between gap-3 px-3 py-1.5 text-left text-sm',
                    index === cursor ? 'bg-raised text-text' : 'text-dim',
                  )}
                >
                  <span className="truncate">{command.label}</span>
                  <span className="label shrink-0">{command.group}</span>
                </button>
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  )
}
